import type * as api from '@gitbook/api-client';
import type { ContentOwnership } from '@gitbook/app-database';
import { createPalette, type PaletteCommandDef, type PaletteDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { filterOutNullable } from '@gitbook/util-ts';

import { apiResource, readAPICache } from '../../state';
import { OrganizationIcon } from '../Organization';

type OnPickFn = (owner: ContentOwnership) => void;

interface PickContentOwnerPaletteOptions {
    /**
     * Callback when a collection/organization is picked
     */
    onPick: OnPickFn;

    /**
     * Optional filter to prevent displaying some entities.
     */
    filter?: (ancestors: ContentOwnership[]) => boolean;
}

/**
 * Create a palette to pick a new content owner (organization or collection).
 */
export function createPickContentOwnerPalette(options: PickContentOwnerPaletteOptions): PaletteDef {
    const { onPick, filter } = options;

    return createPalette({
        placeholder: 'No content found',
        groups: [
            {
                key: 'organizations',
                commands: () => {
                    const { items: allOrganizations } = readAPICache(
                        apiResource('listOrganizationsForAuthenticatedUser', { limit: 100 })
                    );

                    const organizations = filter
                        ? allOrganizations.filter((org) => filter([{ organization: org.id }]))
                        : allOrganizations;

                    // If only one org is available, flatten the palette options
                    // to reduce complexity
                    if (organizations.length === 1) {
                        const org = organizations[0];
                        const owner = { organization: org.id };

                        return [
                            ...createPickTopLevelCommands(owner, onPick),
                            ...createPickCollectionInOrgCommands(org.id, options),
                        ];
                    }

                    return organizations.map<PaletteCommandDef>((org) => {
                        // TODO: handle the case where user has no permissions to put at the top level
                        // it could be done once we have organization.permissions
                        // but not critical for now
                        const owner = { organization: org.id };
                        return {
                            key: org.id,
                            icon: <OrganizationIcon organization={org} />,
                            title: org.title,
                            descendant: () => {
                                return createPalette({
                                    groups: [
                                        {
                                            key: 'top-level',
                                            commands: createPickTopLevelCommands(owner, onPick),
                                        },
                                        {
                                            key: 'collections',
                                            title: 'Or move to collection…',
                                            commands: () =>
                                                createPickCollectionInOrgCommands(org.id, options),
                                        },
                                    ],
                                });
                            },
                        };
                    });
                },
            },
        ],
    });
}

/**
 * Create the palette commands for picking the top level options in a space.
 */
function createPickTopLevelCommands(
    owner: ContentOwnership,
    onPick: OnPickFn
): PaletteCommandDef[] {
    return [
        {
            key: 'top-level-spaces',
            icon: Icon.Space,
            title: 'Top-level',
            trigger: () => onPick(owner),
        },
    ];
}

/**
 * Create the commands to pick a collection in an organization.
 */
function createPickCollectionInOrgCommands(
    organizationId: string,
    options: PickContentOwnerPaletteOptions
) {
    const allContent = readAPICache(
        apiResource('getOrganizationAllContent', {
            organizationId,
        }),
        { staleWhileRevalidate: true }
    );

    return createPickCollectionCommands(
        {
            ancestors: [{ organization: organizationId }],
            items: allContent.spaces,
        },
        options
    );
}

/**
 * Create the commands to pick a new collection in an owner
 */
function createPickCollectionCommands(
    input: {
        items: Array<api.OrganizationAllCollection | api.OrganizationAllSpace>;
        ancestors: ContentOwnership[];
    },
    options: PickContentOwnerPaletteOptions
): PaletteCommandDef[] {
    const { items, ancestors } = input;
    const { onPick, filter } = options;
    const owner = ancestors[ancestors.length - 1];

    return items
        .map((item) => {
            if (!('collection' in item)) {
                return null;
            }
            const { collection } = item;

            const collectionRef = { ...owner, collection: collection.id };
            const collectionAncestors = [...ancestors, collectionRef];
            if (filter?.(collectionAncestors) === false) {
                return null;
            }

            const command = {
                key: collection.id,
                icon: Icon.Collection,
                title: collection.title,
                trigger: () => onPick(collectionRef),
            };

            const subCommands = createPickCollectionCommands(
                {
                    ancestors: collectionAncestors,
                    items: item.content,
                },
                options
            );

            if (subCommands.length === 0) {
                // Prompt to select the collection
                return command;
            }

            // Otherwise show the nested
            return {
                ...command,
                descendant: () => {
                    return createPalette({
                        groups: [
                            {
                                key: 'subcollections',
                                commands: subCommands,
                            },
                        ],
                    });
                },
            };
        })
        .filter(filterOutNullable);
}
